import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Loader2, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api, { teamAPI, partnersAPI, imagesAPI } from '../../services/api';

const TeamManagement = () => {
  const [loading, setLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [teamRes, partnersRes] = await Promise.all([
        teamAPI.getAll(),
        partnersAPI.getAll(),
      ]);
      setTeamMembers(Array.isArray(teamRes.data) ? teamRes.data : (teamRes.data.team || []));
      setPartners(Array.isArray(partnersRes.data) ? partnersRes.data : (partnersRes.data.partners || []));
    } catch (error) {
      console.error('Error loading team/partners:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await teamAPI.update({ team: teamMembers });
      await partnersAPI.update({ partners });
      toast.success("Équipe et partenaires mis à jour");
    } catch (error) {
      console.error('Error saving team/partners:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const addTeamMember = () => {
    setTeamMembers([...teamMembers, { name: '', role: '', bio: '', image: '' }]);
  };

  const removeTeamMember = (index) => {
    setTeamMembers(teamMembers.filter((_, i) => i !== index));
  };

  const updateTeamMember = (index, field, value) => {
    const updated = [...teamMembers];
    updated[index][field] = value;
    setTeamMembers(updated);
  };

  const handleMemberImageUpload = async (index, file) => {
    if (!file) return;
    try {
      const res = await imagesAPI.upload(file);
      const updated = [...teamMembers];
      updated[index].avatar = res.data?.url || res.data?.file_path || '';
      setTeamMembers(updated);
      toast.success('Photo importée');
    } catch (error) {
      console.error('Error uploading member image:', error);
      toast.error("Échec de l'import de la photo");
    }
  };

  const addPartner = () => {
    setPartners([...partners, { name: '', logo: '', website: '' }]);
  };

  const removePartner = (index) => {
    setPartners(partners.filter((_, i) => i !== index));
  };

  const updatePartner = (index, field, value) => {
    const updated = [...partners];
    updated[index][field] = value;
    setPartners(updated);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Gestion de l'Équipe</h2>
        <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
          <Save className="w-4 h-4 mr-2" />
          Sauvegarder
        </Button>
      </div>

      {/* Team Members */}
      <div className="glass rounded-xl p-6 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-semibold text-white flex items-center gap-2">
            <Users className="w-5 h-5" />
            Membres de l'Équipe
          </h3>
          <Button onClick={addTeamMember} size="sm" className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </Button>
        </div>

        {teamMembers.map((member, index) => (
          <div key={index} className="bg-slate-700/50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-300 font-medium">Membre #{index + 1}</span>
              <Button
                onClick={() => removeTeamMember(index)}
                size="sm"
                variant="destructive"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                value={member.name}
                onChange={(e) => updateTeamMember(index, 'name', e.target.value)}
                placeholder="Nom complet"
                className="bg-slate-600 border-slate-500 text-white"
              />
              <Input
                value={member.role}
                onChange={(e) => updateTeamMember(index, 'role', e.target.value)}
                placeholder="Rôle / Poste"
                className="bg-slate-600 border-slate-500 text-white"
              />
            </div>

            <textarea
              value={member.bio}
              onChange={(e) => updateTeamMember(index, 'bio', e.target.value)}
              placeholder="Biographie"
              className="w-full bg-slate-600 border border-slate-500 rounded-md p-2 text-white min-h-[80px]"
            />

            <Input
              value={member.avatar || member.image || ''}
              onChange={(e) => updateTeamMember(index, 'avatar', e.target.value)}
              placeholder="URL de la photo (avatar)"
              className="bg-slate-600 border-slate-500 text-white"
            />
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleMemberImageUpload(index, e.target.files?.[0])}
                className="text-sm text-gray-300"
              />
              <span className="text-xs text-gray-400">Importer depuis le PC</span>
            </div>
          </div>
        ))}
      </div>

      {/* Partners */}
      <div className="glass rounded-xl p-6 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-semibold text-white">Partenaires</h3>
          <Button onClick={addPartner} size="sm" className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </Button>
        </div>

        {partners.map((partner, index) => (
          <div key={index} className="bg-slate-700/50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-300 font-medium">Partenaire #{index + 1}</span>
              <Button
                onClick={() => removePartner(index)}
                size="sm"
                variant="destructive"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input
                value={partner.name}
                onChange={(e) => updatePartner(index, 'name', e.target.value)}
                placeholder="Nom du partenaire"
                className="bg-slate-600 border-slate-500 text-white"
              />
              <Input
                value={partner.logo}
                onChange={(e) => updatePartner(index, 'logo', e.target.value)}
                placeholder="URL du logo"
                className="bg-slate-600 border-slate-500 text-white"
              />
              <Input
                value={partner.website}
                onChange={(e) => updatePartner(index, 'website', e.target.value)}
                placeholder="Site web"
                className="bg-slate-600 border-slate-500 text-white"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TeamManagement;
